import { QueueItem } from '@/api/callcenter/dashboard';
import { useCallCenterDashboard } from '@/api/callcenter/dashboard/getDashboard';
import { Alert } from '@/components/Elements/Alert/Alert';
import { ScrollableLayout } from '@/components/Layout';
import Page from '@/components/Layout/Page';
import { Button } from '@/components/ui/elements/button';
import { Heading2 } from '@/components/ui/typography/Typography';
import { PolicyGate } from '@/features/auth/authorization';
import { ConnectionErrorState } from '@/features/callcenter/components/ConnectionErrorState';
import { EligibilityBanner } from '@/features/callcenter/components/EligibilityBanner';
import { EventsInbox } from '@/features/callcenter/components/EventsInbox';
import { FeedPauseSwitch } from '@/features/callcenter/components/FeedPauseSwitch';
import { QueueList } from '@/features/callcenter/components/QueueList';
import { ReleaseLeadDialog } from '@/features/callcenter/components/ReleaseLeadDialog';
import { StatsBar } from '@/features/callcenter/components/StatsBar';
import { useBrightPatternDial } from '@/features/callcenter/hooks/useBrightPatternDial';
import { useCallCenterHeartbeat } from '@/features/callcenter/hooks/useCallCenterHeartbeat';
import { useOpenAssignment } from '@/features/callcenter/hooks/useOpenAssignment';
import { EventLookup } from '@/features/dashboard/components/EventLookup';
import { LeadLookup } from '@/features/dashboard/components/LeadLookup';
import { NewLeadForm } from '@/features/dashboard/components/NewLeadForm';
import { AuthContext } from '@/providers/auth';
import useCallCenterStore from '@/stores/callcenter';
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useContext, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

export const CallCenterDashboard = () => {
  useCallCenterHeartbeat();

  const { user } = useContext(AuthContext);
  const { data, isPending, isError, dataUpdatedAt, refetch } = useCallCenterDashboard();
  const { openAssignment, pendingAssignmentId } = useOpenAssignment();
  const { status: dialStatus, dial } = useBrightPatternDial();
  const { soundMuted, toggleSoundMuted } = useCallCenterStore(
    useShallow((state) => ({
      soundMuted: state.soundMuted,
      toggleSoundMuted: state.toggleSoundMuted,
    })),
  );
  const [releaseTarget, setReleaseTarget] = useState<QueueItem | null>(null);

  // A refetch failure with no cached data yet (first load, or a cache that
  // was cleared) has nothing usable to show -- a full calm error state with
  // a manual Retry replaces the body instead of rendering an empty queue
  // that would misleadingly read as "no leads" rather than "couldn't load".
  // Once ANY fetch has ever succeeded, `data` stays populated through a
  // later failed refetch (React Query keeps last-good data on error), so
  // this branch naturally only applies to the true no-data case.
  const showFullError = isError && !data;

  return (
    <ScrollableLayout>
      <Page>
        <Page.Header className="mx-4">
          <Page.HeaderContent title={`Hi, ${user?.first_name}!`} />
          <Page.HeaderAction className="items-center gap-3">
            {/* Team View left the main nav deliberately -- managers reach it
                from here (agent-managers) or from the standard dashboard. */}
            <PolicyGate policy="callcenter.manager">
              <Button asChild variant="outline">
                <Link to="/callcenter/team">Team View</Link>
              </Button>
            </PolicyGate>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={soundMuted ? 'Unmute notification sounds' : 'Mute notification sounds'}
              onClick={toggleSoundMuted}
            >
              {soundMuted ? (
                <SpeakerXMarkIcon aria-hidden="true" />
              ) : (
                <SpeakerWaveIcon aria-hidden="true" />
              )}
            </Button>
            <FeedPauseSwitch feedPaused={data?.agent.feed_paused ?? false} />
          </Page.HeaderAction>
        </Page.Header>
        {/* No `policy` prop here: access is gated by the user-level is_callcenter flag
            (see Dashboard.tsx), not an ACL permission, so a Newbie-ACL callcenter
            agent must still be able to see this body. */}
        <Page.Body className="@container space-y-4 p-4 py-2">
          <div className="grid grid-cols-1 @4xl:grid-cols-2 gap-2">
            <PolicyGate policy="leads.create">
              <NewLeadForm />
            </PolicyGate>
            <div className="grid grid-cols-1 @lg:grid-cols-2 gap-2">
              <PolicyGate policy="leads.view">
                <LeadLookup />
              </PolicyGate>
              <PolicyGate policy="events.view">
                <EventLookup />
              </PolicyGate>
            </div>
          </div>
          {showFullError ? (
            <ConnectionErrorState
              data-testid="dashboard-error-state"
              title="Couldn't load your queue"
              description="Check your connection and try again."
              onRetry={() => void refetch()}
            />
          ) : (
            <>
              {isError && (
                <Alert title="Reconnecting…" color="yellow" size="sm">
                  <p>We lost touch with the server. Showing your last known queue.</p>
                </Alert>
              )}
              <EligibilityBanner agent={data?.agent} />
              <StatsBar stats={data?.stats} />
              <div className="grid grid-cols-1 gap-4 @4xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div data-testid="callcenter-dashboard">
                  <Heading2>Your queue</Heading2>
                  <QueueList
                    queue={data?.queue ?? []}
                    agentOnCall={data?.agent.on_call ?? false}
                    dataUpdatedAt={dataUpdatedAt}
                    isLoading={isPending}
                    onOpenItem={openAssignment}
                    onReleaseItem={setReleaseTarget}
                    onDialItem={dial}
                    dialStatus={dialStatus}
                    pendingAssignmentId={pendingAssignmentId}
                  />
                </div>
                {/* Deliberately not behind PolicyGate policy="events.view": ACL
                    resolves from location_access for the ACTIVE location, and a
                    callcenter agent's active location (the USA franchise, 0)
                    typically has no access record — they fall back to the Newbie
                    ACL, which denies events.view. Same reasoning as the ungated
                    body above: is_callcenter is the access flag here, and the
                    events API itself authorizes callcenter users. */}
                <EventsInbox />
              </div>
            </>
          )}
        </Page.Body>
      </Page>
      <ReleaseLeadDialog
        assignment={releaseTarget}
        onOpenChange={(open) => {
          if (!open) setReleaseTarget(null);
        }}
      />
    </ScrollableLayout>
  );
};
